import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Merchant, MerchantResponse, ApiResponse } from '../../types';
import { apiService } from '../../services/api';

// Async thunk for fetching merchants with pagination
export const fetchMerchants = createAsyncThunk(
    'merchants/fetchMerchants',
    async (
        { limit = 10, offset = 0 }: { limit?: number; offset?: number },
        { rejectWithValue }
    ) => {
        try {
            const response: ApiResponse<MerchantResponse> =
                await apiService.getMerchants(limit, offset);

            if (response.success) {
                return {
                    merchants: response.data.merchants,
                    limit,
                    offset,
                };
            } else {
                return rejectWithValue(response.message);
            }
        } catch (error) {
            return rejectWithValue(
                error instanceof Error
                    ? error.message
                    : 'Failed to fetch merchants'
            );
        }
    }
);

interface MerchantsState {
    merchants: Merchant[];
    loading: boolean;
    error: string | null;
    pagination: {
        limit: number;
        offset: number;
        total: number;
    };
    lastFetch: string | null;
}

const initialState: MerchantsState = {
    merchants: [],
    loading: false,
    error: null,
    pagination: {
        limit: 10,
        offset: 0,
        total: 0,
    },
    lastFetch: null,
};

const merchantsSlice = createSlice({
    name: 'merchants',
    initialState,
    reducers: {
        clearError: state => {
            state.error = null;
        },
        setPagination: (
            state,
            action: PayloadAction<{ limit: number; offset: number }>
        ) => {
            state.pagination.limit = action.payload.limit;
            state.pagination.offset = action.payload.offset;
        },
    },
    extraReducers: builder => {
        builder
            // Fetch merchants
            .addCase(fetchMerchants.pending, state => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchMerchants.fulfilled, (state, action) => {
                state.loading = false;
                state.merchants = action.payload.merchants;
                state.pagination.limit = action.payload.limit;
                state.pagination.offset = action.payload.offset;
                state.lastFetch = new Date().toISOString();
                state.error = null;
            })
            .addCase(fetchMerchants.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearError, setPagination } = merchantsSlice.actions;
export default merchantsSlice.reducer;
